import React, { useMemo } from "react";

import clsx from "clsx";
import { useLocation, useNavigate } from "react-router";

import RouteDrawer, { RouteDrawerProps } from "components/RouteDrawer";
import makeStyles from "hooks/makeStyles";
import useLocale from "hooks/useLocale";

import MenuModal from "./MenuModal";

const useStyles = makeStyles(theme => ({
	root: {},
	paper: {
		borderRadius: `0 0 ${theme.spacing(2)}px ${theme.spacing(2)}px`,
		backgroundColor: "rgba(33,33,33,0.7)",
		WebkitBackdropFilter: "blur(5px)",
		backdropFilter: "blur(5px)",
		color: "#fff",
		padding: theme.spacing(2),
		display: "flex"
	},
	backdrop: {
		backgroundColor: "rgba(0,0,0,0.1)",
		[theme.breakpoints.up(480)]: {
			backgroundColor: "transparent",
			WebkitBackdropFilter: "blur(1.8px)",
			backdropFilter: "blur(1.8px)"
		}
	}
}));

type Props = Omit<RouteDrawerProps, "swipeable" | "matchRoute"> & {
	selectedMenuSlug: null | string;
};

const MenuDrawer = ({ className, selectedMenuSlug, ...otherProps }: Props) => {
	const { locale } = useLocale();
	const { hash } = useLocation();
	const navigate = useNavigate();
	const classes = useStyles();

	const close = useMemo(
		() => () => {
			if (hash === "#menu?") {
				navigate("#");
			}
		},
		[hash, navigate]
	);

	const handleChange = useMemo(
		() => (value: string) => {
			navigate(`/${locale}/${value}`);
		},
		[locale, navigate]
	);

	const props: RouteDrawerProps = useMemo(
		() =>
			Object.assign(
				{
					className: clsx(classes.root, className),
					classes: { paper: classes.paper },
					BackdropProps: {
						className: classes.backdrop
					},
					anchor: "top",
					disableSwipeToOpen: true,
					swipeable: true,
					matchRoute: ({ location: { hash } }) => hash === "#menu?",
					onClose: close,
					onOpen: () => {}
				},
				otherProps
			),
		[className, classes.backdrop, classes.paper, classes.root, close, otherProps]
	);

	return (
		<RouteDrawer {...props}>
			<MenuModal selectedMenuSlug={selectedMenuSlug} onChange={handleChange} onClose={close} />
		</RouteDrawer>
	);
};

export default MenuDrawer;
